import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import supplierMasterService from '../../services/supplierMasterService'

const SupplierMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const supplierData = location.state?.supplierData

  const [formData, setFormData] = useState({
    supplierCode: '',
    supplierName: '',
    supplierType: 'Vendor',
    businessName: '',
    legalEntityName: '',
    registrationNumber: '',
    taxId: '',
    gstNumber: '',
    panNumber: '',
    primaryContact: {
      contactPerson: '',
      email: '',
      phone: '',
      mobile: ''
    },
    billingAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India'
    },
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India'
    },
    additionalContacts: [],
    bankingDetails: {
      bankName: '',
      accountNumber: '',
      accountHolderName: '',
      ifscCode: '',
      branchName: '',
      accountType: 'Current'
    },
    paymentTerms: {
      paymentMethod: 'Bank Transfer',
      creditDays: 0,
      creditLimit: 0,
      currency: 'INR'
    },
    rating: 0,
    performanceMetrics: {
      onTimeDelivery: 0,
      qualityRating: 0,
      responseTime: 0
    },
    supplierCategories: [],
    status: 'Active',
    website: '',
    notes: ''
  })

  const [isLoadingSupplierCode, setIsLoadingSupplierCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newContact, setNewContact] = useState({
    contactPerson: '',
    designation: '',
    email: '',
    phone: '',
    mobile: '',
    isPrimary: false
  })

  // Fetch next supplier code (only for new suppliers)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchSupplierCode = async () => {
        try {
          setIsLoadingSupplierCode(true)
          const response = await supplierMasterService.getNextSupplierCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              supplierCode: response.data.supplierCode || response.data.formattedValue || 'SUP0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching supplier code:', error)
          setFormData(prev => ({ ...prev, supplierCode: 'SUP0001' }))
        } finally {
          setIsLoadingSupplierCode(false)
        }
      }
      fetchSupplierCode()
    }
  }, [isEditMode, isViewMode])

  // Load supplier data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && supplierData?._id) {
      loadSupplierData(supplierData._id)
    }
  }, [isEditMode, isViewMode, supplierData])

  const loadSupplierData = async (id) => {
    try {
      setIsLoading(true)
      const response = await supplierMasterService.getSupplierById(id)
      if (response.success && response.data) {
        const supplier = response.data
        setFormData({
          supplierCode: supplier.supplierCode || '',
          supplierName: supplier.supplierName || '',
          supplierType: supplier.supplierType || 'Vendor',
          businessName: supplier.businessName || '',
          legalEntityName: supplier.legalEntityName || '',
          registrationNumber: supplier.registrationNumber || '',
          taxId: supplier.taxId || '',
          gstNumber: supplier.gstNumber || '',
          panNumber: supplier.panNumber || '',
          primaryContact: supplier.primaryContact || {
            contactPerson: '',
            email: '',
            phone: '',
            mobile: ''
          },
          billingAddress: supplier.billingAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          shippingAddress: supplier.shippingAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          additionalContacts: supplier.additionalContacts || [],
          bankingDetails: supplier.bankingDetails || {
            bankName: '',
            accountNumber: '',
            accountHolderName: '',
            ifscCode: '',
            branchName: '',
            accountType: 'Current'
          },
          paymentTerms: supplier.paymentTerms || {
            paymentMethod: 'Bank Transfer',
            creditDays: 0,
            creditLimit: 0,
            currency: 'INR'
          },
          rating: supplier.rating || 0,
          performanceMetrics: supplier.performanceMetrics || {
            onTimeDelivery: 0,
            qualityRating: 0,
            responseTime: 0
          },
          supplierCategories: supplier.supplierCategories || [],
          status: supplier.status || 'Active',
          website: supplier.website || '',
          notes: supplier.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading supplier data:', error)
      toast.error('Failed to load supplier data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('primaryContact.')) {
      const contactField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        primaryContact: {
          ...prev.primaryContact,
          [contactField]: value
        }
      }))
    } else if (name.startsWith('billingAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          [addressField]: value
        }
      }))
    } else if (name.startsWith('shippingAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        shippingAddress: {
          ...prev.shippingAddress,
          [addressField]: value
        }
      }))
    } else if (name.startsWith('bankingDetails.')) {
      const bankingField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        bankingDetails: {
          ...prev.bankingDetails,
          [bankingField]: value
        }
      }))
    } else if (name.startsWith('paymentTerms.')) {
      const paymentField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        paymentTerms: {
          ...prev.paymentTerms,
          [paymentField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('performanceMetrics.')) {
      const metricField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        performanceMetrics: {
          ...prev.performanceMetrics,
          [metricField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name === 'supplierCategories') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
      setFormData(prev => ({
        ...prev,
        supplierCategories: selectedOptions
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addContact = () => {
    if (!newContact.contactPerson.trim()) {
      toast.error('Please enter a contact person name')
      return
    }
    
    setFormData(prev => ({
      ...prev,
      additionalContacts: [...prev.additionalContacts, { ...newContact }]
    }))
    
    setNewContact({
      contactPerson: '',
      designation: '',
      email: '',
      phone: '',
      mobile: '',
      isPrimary: false
    })
  }

  const removeContact = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalContacts: prev.additionalContacts.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.supplierName) {
        toast.error('Please fill in all required fields (Supplier Name)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && supplierData?._id) {
        const response = await supplierMasterService.updateSupplier(supplierData._id, formData)
        if (response.success) {
          toast.success('Supplier updated successfully!')
          navigate('/procurement/supplier-master')
        } else {
          toast.error(response.message || 'Failed to update supplier')
        }
      } else {
        const response = await supplierMasterService.createSupplier(formData)
        if (response.success) {
          toast.success('Supplier created successfully!')
          navigate('/procurement/supplier-master')
        } else {
          toast.error(response.message || 'Failed to create supplier')
        }
      }
    } catch (error) {
      console.error('Error saving supplier:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save supplier')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadSupplierData(supplierData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        supplierName: '',
        supplierType: 'Vendor',
        businessName: '',
        legalEntityName: '',
        registrationNumber: '',
        taxId: '',
        gstNumber: '',
        panNumber: '',
        primaryContact: {
          contactPerson: '',
          email: '',
          phone: '',
          mobile: ''
        },
        billingAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India'
        },
        shippingAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India'
        },
        additionalContacts: [],
        bankingDetails: {
          bankName: '',
          accountNumber: '',
          accountHolderName: '',
          ifscCode: '',
          branchName: '',
          accountType: 'Current'
        },
        paymentTerms: {
          paymentMethod: 'Bank Transfer',
          creditDays: 0,
          creditLimit: 0,
          currency: 'INR'
        },
        rating: 0,
        performanceMetrics: {
          onTimeDelivery: 0,
          qualityRating: 0,
          responseTime: 0
        },
        supplierCategories: [],
        status: 'Active',
        website: '',
        notes: ''
      }))
      setNewContact({
        contactPerson: '',
        designation: '',
        email: '',
        phone: '',
        mobile: '',
        isPrimary: false
      })
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Suppliers', path: '/procurement/supplier-master' },
    { 
      label: isEditMode ? 'Edit Supplier' : isViewMode ? 'View Supplier' : 'New Supplier', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading supplier...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Supplier' : isViewMode ? 'View Supplier' : 'New Supplier'}</h1>
          <p>{isEditMode ? 'Update supplier information' : isViewMode ? 'View supplier information' : 'Create a new supplier profile'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/supplier-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Supplier Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Supplier Code, Supplier Name, Supplier Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="supplierCode" className="form-label required">
                Supplier Code
              </label>
              <input
                type="text"
                id="supplierCode"
                name="supplierCode"
                value={isLoadingSupplierCode ? 'Generating...' : formData.supplierCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="supplierName" className="form-label required">
                Supplier Name
              </label>
              <input
                type="text"
                id="supplierName"
                name="supplierName"
                value={formData.supplierName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., ABC Suppliers Pvt Ltd"
              />
            </div>

            <div className="form-group">
              <label htmlFor="supplierType" className="form-label">
                Supplier Type
              </label>
              <select
                id="supplierType"
                name="supplierType"
                value={formData.supplierType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Vendor">Vendor</option>
                <option value="Service Provider">Service Provider</option>
                <option value="Manufacturer">Manufacturer</option>
                <option value="Distributor">Distributor</option>
                <option value="Wholesaler">Wholesaler</option>
                <option value="Retailer">Retailer</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
                <option value="Blacklisted">Blacklisted</option>
              </select>
            </div>
          </div>

          {/* Row 2: Business Name, Legal Entity Name, Registration Number, Tax ID */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="businessName" className="form-label">
                Business Name
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Business trading name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="legalEntityName" className="form-label">
                Legal Entity Name
              </label>
              <input
                type="text"
                id="legalEntityName"
                name="legalEntityName"
                value={formData.legalEntityName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Registered legal name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="registrationNumber" className="form-label">
                Registration Number
              </label>
              <input
                type="text"
                id="registrationNumber"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Company registration number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxId" className="form-label">
                Tax ID
              </label>
              <input
                type="text"
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Tax identification number"
              />
            </div>
          </div>

          {/* Row 3: GST Number, PAN Number, Website, Rating */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="gstNumber" className="form-label">
                GST Number
              </label>
              <input
                type="text"
                id="gstNumber"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="GST registration number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="panNumber" className="form-label">
                PAN Number
              </label>
              <input
                type="text"
                id="panNumber"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="PAN number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="website" className="form-label">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="https://example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="rating" className="form-label">
                Rating (0-5)
              </label>
              <input
                type="number"
                id="rating"
                name="rating"
                value={formData.rating}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="5"
                step="0.1"
              />
            </div>
          </div>

          {/* Primary Contact */}
          <div className="section-header"><h3>Primary Contact</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="primaryContact.contactPerson" className="form-label">
                Contact Person
              </label>
              <input
                type="text"
                id="primaryContact.contactPerson"
                name="primaryContact.contactPerson"
                value={formData.primaryContact.contactPerson}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Contact person name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="primaryContact.email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="primaryContact.email"
                name="primaryContact.email"
                value={formData.primaryContact.email}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="email@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="primaryContact.phone" className="form-label">
                Phone
              </label>
              <input
                type="text"
                id="primaryContact.phone"
                name="primaryContact.phone"
                value={formData.primaryContact.phone}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="primaryContact.mobile" className="form-label">
                Mobile
              </label>
              <input
                type="text"
                id="primaryContact.mobile"
                name="primaryContact.mobile"
                value={formData.primaryContact.mobile}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Mobile number"
              />
            </div>
          </div>

          {/* Billing Address */}
          <div className="section-header"><h3>Billing Address</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="billingAddress.street" className="form-label">
                Street
              </label>
              <input
                type="text"
                id="billingAddress.street"
                name="billingAddress.street"
                value={formData.billingAddress.street}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Street address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingAddress.city" className="form-label">
                City
              </label>
              <input
                type="text"
                id="billingAddress.city"
                name="billingAddress.city"
                value={formData.billingAddress.city}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="City"
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingAddress.state" className="form-label">
                State
              </label>
              <input
                type="text"
                id="billingAddress.state"
                name="billingAddress.state"
                value={formData.billingAddress.state}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="State"
              />
            </div>

            <div className="form-group">
              <label htmlFor="billingAddress.postalCode" className="form-label">
                Postal Code
              </label>
              <input
                type="text"
                id="billingAddress.postalCode"
                name="billingAddress.postalCode"
                value={formData.billingAddress.postalCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Postal code"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="billingAddress.country" className="form-label">
              Country
            </label>
            <input
              type="text"
              id="billingAddress.country"
              name="billingAddress.country"
              value={formData.billingAddress.country}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              placeholder="Country"
            />
          </div>

          {/* Shipping Address */}
          <div className="section-header"><h3>Shipping Address</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="shippingAddress.street" className="form-label">
                Street
              </label>
              <input
                type="text"
                id="shippingAddress.street"
                name="shippingAddress.street"
                value={formData.shippingAddress.street}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Street address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shippingAddress.city" className="form-label">
                City
              </label>
              <input
                type="text"
                id="shippingAddress.city"
                name="shippingAddress.city"
                value={formData.shippingAddress.city}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="City"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shippingAddress.state" className="form-label">
                State
              </label>
              <input
                type="text"
                id="shippingAddress.state"
                name="shippingAddress.state"
                value={formData.shippingAddress.state}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="State"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shippingAddress.postalCode" className="form-label">
                Postal Code
              </label>
              <input
                type="text"
                id="shippingAddress.postalCode"
                name="shippingAddress.postalCode"
                value={formData.shippingAddress.postalCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Postal code"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="shippingAddress.country" className="form-label">
              Country
            </label>
            <input
              type="text"
              id="shippingAddress.country"
              name="shippingAddress.country"
              value={formData.shippingAddress.country}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              placeholder="Country"
            />
          </div>

          {/* Additional Contacts */}
          <div className="section-header"><h3>Additional Contacts</h3></div>

          {formData.additionalContacts.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.additionalContacts.map((contact, index) => (
                <div key={index} style={{ 
                  marginBottom: '12px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong>{contact.contactPerson}</strong>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {contact.designation && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>{contact.designation}</div>}
                  {contact.email && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>Email: {contact.email}</div>}
                  {contact.phone && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>Phone: {contact.phone}</div>}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    value={newContact.contactPerson}
                    onChange={(e) => setNewContact({ ...newContact, contactPerson: e.target.value })}
                    className="form-input"
                    placeholder="Name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    value={newContact.designation}
                    onChange={(e) => setNewContact({ ...newContact, designation: e.target.value })}
                    className="form-input"
                    placeholder="Designation"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    className="form-input"
                    placeholder="email@example.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    className="form-input"
                    placeholder="Phone"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={addContact}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Contact
              </button>
            </div>
          )}

          {/* Banking Details */}
          <div className="section-header"><h3>Banking Details</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="bankingDetails.bankName" className="form-label">
                Bank Name
              </label>
              <input
                type="text"
                id="bankingDetails.bankName"
                name="bankingDetails.bankName"
                value={formData.bankingDetails.bankName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Bank name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankingDetails.accountNumber" className="form-label">
                Account Number
              </label>
              <input
                type="text"
                id="bankingDetails.accountNumber"
                name="bankingDetails.accountNumber"
                value={formData.bankingDetails.accountNumber}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Account number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankingDetails.accountHolderName" className="form-label">
                Account Holder Name
              </label>
              <input
                type="text"
                id="bankingDetails.accountHolderName"
                name="bankingDetails.accountHolderName"
                value={formData.bankingDetails.accountHolderName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Account holder name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankingDetails.ifscCode" className="form-label">
                IFSC Code
              </label>
              <input
                type="text"
                id="bankingDetails.ifscCode"
                name="bankingDetails.ifscCode"
                value={formData.bankingDetails.ifscCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="IFSC code"
              />
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="bankingDetails.branchName" className="form-label">
                Branch Name
              </label>
              <input
                type="text"
                id="bankingDetails.branchName"
                name="bankingDetails.branchName"
                value={formData.bankingDetails.branchName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Branch name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bankingDetails.accountType" className="form-label">
                Account Type
              </label>
              <select
                id="bankingDetails.accountType"
                name="bankingDetails.accountType"
                value={formData.bankingDetails.accountType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Savings">Savings</option>
                <option value="Current">Current</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="section-header"><h3>Payment Terms</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="paymentTerms.paymentMethod" className="form-label">
                Payment Method
              </label>
              <select
                id="paymentTerms.paymentMethod"
                name="paymentTerms.paymentMethod"
                value={formData.paymentTerms.paymentMethod}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentTerms.creditDays" className="form-label">
                Credit Days
              </label>
              <input
                type="number"
                id="paymentTerms.creditDays"
                name="paymentTerms.creditDays"
                value={formData.paymentTerms.creditDays}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentTerms.creditLimit" className="form-label">
                Credit Limit
              </label>
              <input
                type="number"
                id="paymentTerms.creditLimit"
                name="paymentTerms.creditLimit"
                value={formData.paymentTerms.creditLimit}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentTerms.currency" className="form-label">
                Currency
              </label>
              <select
                id="paymentTerms.currency"
                name="paymentTerms.currency"
                value={formData.paymentTerms.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="section-header"><h3>Performance Metrics</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="performanceMetrics.onTimeDelivery" className="form-label">
                On-Time Delivery (%)
              </label>
              <input
                type="number"
                id="performanceMetrics.onTimeDelivery"
                name="performanceMetrics.onTimeDelivery"
                value={formData.performanceMetrics.onTimeDelivery}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="performanceMetrics.qualityRating" className="form-label">
                Quality Rating (0-5)
              </label>
              <input
                type="number"
                id="performanceMetrics.qualityRating"
                name="performanceMetrics.qualityRating"
                value={formData.performanceMetrics.qualityRating}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="5"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="performanceMetrics.responseTime" className="form-label">
                Response Time (Hours)
              </label>
              <input
                type="number"
                id="performanceMetrics.responseTime"
                name="performanceMetrics.responseTime"
                value={formData.performanceMetrics.responseTime}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default SupplierMasterForm

